import { toast as baseToast, type Id, type ToastContent, type ToastOptions } from 'react-toastify';

/**
 * The one toast API for the portal. Import `toast` from here instead of 'react-toastify' or 'sonner'.
 *
 * Rules, so toasts look and behave the same everywhere:
 * - Errors, warnings, explicit email confirmations and explicit action confirmations (`toast.confirm`) are shown.
 *   Other success and info calls show nothing.
 * - One status toast at a time, always in the container's position (set in app/layout.tsx).
 *   Per-call position, style and className options are ignored.
 * - Messages that arrive while the current toast is still fresh (one action often reports the same
 *   failure twice, e.g. the API error handler and the page's catch block) are dropped, so the first,
 *   usually most specific, message stays. A later message replaces the current one.
 * - Plain `toast(content, options)` calls (live portal notifications) pass through unchanged.
 */

type StatusType = 'error' | 'warning' | 'success';

const AUTO_CLOSE_MS: Record<StatusType, number> = { error: 5000, warning: 4000, success: 4000 };
// Follow-up messages from the same action arrive within this window
const SAME_ACTION_WINDOW_MS = 1500;

let currentStatusToastId: Id | null = null;
let currentStatusShownAt = 0;
let nextStatusToastNumber = 0;

const showStatusToast = (type: StatusType, content: ToastContent): Id => {
  const now = Date.now();
  if (currentStatusToastId !== null && baseToast.isActive(currentStatusToastId)) {
    if (type !== 'success' && now - currentStatusShownAt < SAME_ACTION_WINDOW_MS) return currentStatusToastId;
    baseToast.dismiss(currentStatusToastId);
  }

  nextStatusToastNumber += 1;
  currentStatusToastId = `portal-status-${nextStatusToastNumber}`;
  currentStatusShownAt = now;
  return baseToast[type](content, { toastId: currentStatusToastId, autoClose: AUTO_CLOSE_MS[type] });
};

// Accepted for compatibility; success and info messages are intentionally not shown
const ignoreToast = (_content?: ToastContent, _options?: ToastOptions): Id => '';

export const toast = Object.assign(
  (content: ToastContent, options?: ToastOptions): Id => baseToast(content, options),
  {
    error: (content: ToastContent, _options?: ToastOptions): Id => showStatusToast('error', content),
    warning: (content: ToastContent, _options?: ToastOptions): Id => showStatusToast('warning', content),
    warn: (content: ToastContent, _options?: ToastOptions): Id => showStatusToast('warning', content),
    success: ignoreToast,
    emailSent: (content: ToastContent, _options?: ToastOptions): Id => showStatusToast('success', content),
    // A success the user must see, e.g. Apply Changes on the package modification page
    confirm: (content: ToastContent, _options?: ToastOptions): Id => showStatusToast('success', content),
    info: ignoreToast,
    dismiss: (id?: Id) => baseToast.dismiss(id),
    isActive: (id: Id) => baseToast.isActive(id),
  },
);
